import React, { useState, useEffect, useRef } from 'react';

// getGsapModifiers Function
export function getGsapModifiers(animatedBackgroundRef) {
  return {
    x: (x) => {
      const imageRef = animatedBackgroundRef.current.imageRef;
      const container = imageRef.parentElement.parentElement; // Adjust if necessary

      // Parse the width values from the style (e.g., '1000px' -> 1000)
      const imageWidth = parseFloat(imageRef.parentElement.style.width);
      const xOffset = parseFloat(imageRef.parentElement.style.left);
      const containerRect = container.getBoundingClientRect();

      const xValue = parseFloat(x);
      const minX = Math.min(0, containerRect.width - imageWidth - xOffset);
      const maxX = 0;
      const constrainedX = Math.max(minX, Math.min(maxX, xValue));
      return `${constrainedX}px`;
    },
    y: (y) => {
      const imageRef = animatedBackgroundRef.current.imageRef;
      const container = imageRef.parentElement.parentElement; // Adjust if necessary

      const containerRect = container.getBoundingClientRect();
      const imageRect = imageRef.getBoundingClientRect();

      const yValue = parseFloat(y);
      const minY = Math.min(0, containerRect.height - imageRect.height);
      const maxY = 0;
      const constrainedY = Math.max(minY, Math.min(maxY, yValue));
      return `${constrainedY}px`;
    },
  };
}

// AnimatedBackground Component
export const AnimatedBackground = React.forwardRef(({ imageSrc }, ref) => {
  const containerRef = React.useRef(null);
  const wrapperRef = React.useRef(null);
  const imageRef = React.useRef(null);
  const [imageLoaded, setImageLoaded] = React.useState(false);

  React.useImperativeHandle(ref, () => ({
    imageRef: imageRef.current,
  }));

  React.useEffect(() => {
    const updateDimensions = () => {
      if (
        containerRef.current &&
        wrapperRef.current &&
        imageRef.current &&
        imageLoaded
      ) {
        const container = containerRef.current.getBoundingClientRect();
        const image = imageRef.current;

        // Get natural aspect ratio of the image
        const imageAspectRatio = image.naturalWidth / image.naturalHeight;

        // First, try to fit by width
        let width = container.width;
        let height = width / imageAspectRatio;

        // If height isn't enough, scale by height instead
        if (height < container.height) {
          height = container.height;
          width = height * imageAspectRatio;
        }

        // Center the image wrapper
        const left = (container.width - width) / 2;
        const top = (container.height - height) / 2;

        // Apply dimensions to the wrapper
        wrapperRef.current.style.width = `${width}px`;
        wrapperRef.current.style.height = `${height}px`;
        wrapperRef.current.style.left = `${left}px`;
        wrapperRef.current.style.top = `${top}px`;

        // Ensure the image fills the wrapper
        imageRef.current.style.width = '100%';
        imageRef.current.style.height = '100%';
      }
    };

    window.addEventListener('resize', updateDimensions);
    if (imageLoaded) {
      updateDimensions();
    }

    return () => window.removeEventListener('resize', updateDimensions);
  }, [imageLoaded, imageSrc]);

  return (
    <div
      ref={containerRef}
      className="absolute inset-0 overflow-hidden pointer-events-none"
    >
      <div
        ref={wrapperRef}
        style={{
          position: 'absolute',
          overflow: 'hidden',
        }}
      >
        <img
          ref={imageRef}
          src={imageSrc}
          className="max-h-none max-w-none"
          onLoad={() => {
            setImageLoaded(true);
            // Ensure dimensions are updated after image loads
            if (containerRef.current && wrapperRef.current) {
              const event = new Event('resize');
              window.dispatchEvent(event);
            }
          }}
          style={{
            position: 'absolute',
            width: '100%',
            height: '100%',
            opacity: imageLoaded ? 1 : 0,
            transformOrigin: 'center center',
          }}
          alt="background"
        />
      </div>
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,rgba(255,255,255,0.1),transparent_70%)]" />
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,transparent_0%,rgba(0,0,0,0.4)_100%)]" />
    </div>
  );
});
